/**
 * Backlog (scrollback) panel — shows the last N say beats with speaker + text.
 * Opened in-game by pressing B or scrolling up on the dialogue box.
 */

export interface BacklogEntry {
  speaker: string;
  color: string;
  text: string;
}

const MAX_BACKLOG = 60;

/**
 * In-memory backlog store. The ScenePlayer pushes entries here on each say beat.
 */
export class BacklogStore {
  private entries: BacklogEntry[] = [];

  push(entry: BacklogEntry): void {
    this.entries.push(entry);
    if (this.entries.length > MAX_BACKLOG) {
      this.entries = this.entries.slice(-MAX_BACKLOG);
    }
  }

  getAll(): readonly BacklogEntry[] {
    return this.entries;
  }

  clear(): void {
    this.entries = [];
  }
}

export interface BacklogPanelCallbacks {
  onClose: () => void;
}

/**
 * Render the backlog overlay.
 */
export function renderBacklogPanel(
  root: HTMLElement,
  store: BacklogStore,
  callbacks: BacklogPanelCallbacks,
): void {
  // Remove any existing backlog
  const existing = root.querySelector('#backlog-modal');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'backlog-modal';

  const panel = document.createElement('div');
  panel.className = 'sl-panel backlog-panel';

  // Header
  const header = document.createElement('div');
  header.className = 'sl-header';

  const title = document.createElement('h2');
  title.textContent = 'Backlog';
  header.appendChild(title);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'sl-close';
  closeBtn.textContent = '✕';
  closeBtn.addEventListener('click', () => close());
  header.appendChild(closeBtn);
  panel.appendChild(header);

  // Entries
  const list = document.createElement('div');
  list.className = 'backlog-list';

  const entries = store.getAll();
  if (entries.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'backlog-empty';
    empty.textContent = 'No dialogue yet.';
    list.appendChild(empty);
  } else {
    for (const entry of entries) {
      const line = document.createElement('div');
      line.className = 'backlog-line';

      if (entry.speaker) {
        const speaker = document.createElement('span');
        speaker.className = 'backlog-speaker';
        speaker.textContent = entry.speaker;
        speaker.style.color = entry.color || '';
        line.appendChild(speaker);
      }

      const text = document.createElement('span');
      text.className = 'backlog-text';
      text.textContent = entry.text;
      line.appendChild(text);

      list.appendChild(line);
    }
  }

  panel.appendChild(list);
  overlay.appendChild(panel);

  // Scroll to bottom (most recent)
  requestAnimationFrame(() => {
    list.scrollTop = list.scrollHeight;
  });

  // Close on overlay background click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  // Esc or B to close
  const keyHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' || e.key === 'b' || e.key === 'B') {
      e.preventDefault();
      close();
    }
  };
  document.addEventListener('keydown', keyHandler);

  root.appendChild(overlay);

  function close(): void {
    document.removeEventListener('keydown', keyHandler);
    overlay.remove();
    callbacks.onClose();
  }
}
